// DS5.5, DS5.5.1
import React, { useContext, useEffect, useState, useRef } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { useEnabledFlagList } from '@wix/da-react-context/pkg/flags/hooks';
import EmptyWatchedGroups from './_partials/EmptyWatchedGroups';
import NetworkBarLink from './_partials/NetworkBarLink';
import HomeIcon from '@wix/da-ds/pkg/Icons/24x24/Home';
import Eyecon from '@wix/da-ds/pkg/Icons/24x24/FollowingWatch';
import ShopIcon from '@wix/da-ds/pkg/Icons/24x24/Shop';
import PopularIcon from '@wix/da-ds/pkg/Icons/24x24/Popular';
import DailyDeviationIcon from '@wix/da-ds/pkg/Icons/24x24/DailyDeviation';
import TopicsIcon from '@wix/da-ds/pkg/Icons/24x24/Topics';
import NewestIcon from '@wix/da-ds/pkg/Icons/24x24/Newest';
import DreamUpIcon from '@wix/da-ds/pkg/Icons/24x24/DreamUp';
import RecommendedIcon from '@wix/da-ds/pkg/Icons/Recommended';
import AddToGroupFill from '@wix/da-ds/pkg/Icons/16x16/AddToGroupFill';
import GiveFeedbackIcon from '@wix/da-ds/pkg/Icons/24x24/GiveFeedback';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { Nav } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import { watchedGroupsStreamId, recommendedGroupsStreamId, NETWORKBAR_FEEDBACK_URL } from './redux/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { useScrollDescendantIntoView } from './hooks/useScrollDescendantIntoView';
import FoldableGroupList from './_partials/FoldableGroupList';
import NetworkBarItemGroup from './_partials/NetworkBarItemGroup';
import s from './NetworkBar.scss';
const menuItemIconMap = {
    home: HomeIcon,
    watch: Eyecon,
    shop: ShopIcon,
    dailyDeviations: DailyDeviationIcon,
    topics: TopicsIcon,
    popular: PopularIcon,
    new: NewestIcon,
    dreamup: DreamUpIcon
};
const LINKS_SHOWN_ONLY_ON_MOBILE = [
    'shop',
    'dreamup'
];
const NetworkBar = ({ id , initialFetch , isVisible =true , isCollapsed , selectedItem , showWatchedGroupsEmptyState , showRecommendedGroups , className , isInitialized , mainNavItems , canSeeOldWatch  })=>{
    const [hideContent, setHideContent] = useState(true);
    const scrollableRef = useRef(null);
    const { t  } = useTranslation();
    const isMobile = useContext(MobileContext);
    const { isLoggedIn  } = useContext(SessionContext);
    const features = useEnabledFlagList();
    const tabIndex = isVisible ? 0 : -1;
    const mainNavItemsFiltered = mainNavItems.filter((item)=>{
        return isMobile || !LINKS_SHOWN_ONLY_ON_MOBILE.includes(item.key);
    });
    useScrollDescendantIntoView(scrollableRef, s['selected-item'], isInitialized);
    useEffect(()=>{
        if (!isInitialized && isVisible) {
            initialFetch();
        }
        setHideContent(false);
    }, [
        initialFetch,
        isInitialized,
        isVisible
    ]);
    return /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: {
            is_expanded: !isCollapsed ? 1 : 0
        }
    }, /*#__PURE__*/ React.createElement("div", {
        className: classnames(!isMobile && s['scroll-wrapper'], isCollapsed && s['collapsed'], hideContent && s['hidden'], className),
        ref: scrollableRef
    }, /*#__PURE__*/ React.createElement(Nav, {
        className: s['root'],
        id: id
    }, /*#__PURE__*/ React.createElement(VisuallyHidden, null, /*#__PURE__*/ React.createElement(Heading, null, t('common.networkBar'))), /*#__PURE__*/ React.createElement("ul", null, mainNavItemsFiltered.map((item)=>{
        if (!item || item.restrictLoggedIn && !isLoggedIn || item.key === 'new' || item.withFeature && !features.includes(item.withFeature)) {
            return null;
        }
        var _item_pageType, _item_pageType1;
        return /*#__PURE__*/ React.createElement("li", {
            key: item.key
        }, /*#__PURE__*/ React.createElement(NetworkBarLink, {
            label: item.key,
            url: item.url,
            icon: menuItemIconMap[item.key],
            tabIndex: tabIndex,
            isCollapsed: !isMobile && isCollapsed,
            isSelected: selectedItem === ((_item_pageType = item.pageType) !== null && _item_pageType !== void 0 ? _item_pageType : item.key),
            className: classnames(selectedItem === ((_item_pageType1 = item.pageType) !== null && _item_pageType1 !== void 0 ? _item_pageType1 : item.key) && s['selected-item']),
            hasNewContent: item.hasNewContent,
            betaBadge: item.betaBadge,
            showTooltip: true,
            menuItems: isMobile && item.key === 'watch' && canSeeOldWatch ? [
                {
                    label: t('networkBar.navItems.watch.actions.oldWatch'),
                    link: Url.watchLink(),
                    biData: {
                        evid: BiEventType.NETWORK_BAR_3DOTS_SELECT,
                        sectionname: item.key,
                        indicator: item.hasNewContent ? 1 : 0,
                        menu_item: 'oldWatch'
                    }
                }
            ] : undefined
        }));
    })), showWatchedGroupsEmptyState && !isCollapsed && /*#__PURE__*/ React.createElement("div", {
        className: s['group']
    }, /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: {
            sectionname: 'groups'
        }
    }, /*#__PURE__*/ React.createElement(NetworkBarItemGroup, {
        title: t('networkBar.subheader.groups'),
        icon: AddToGroupFill,
        isCollapsed: isCollapsed
    }, /*#__PURE__*/ React.createElement(EmptyWatchedGroups, null)))), !showWatchedGroupsEmptyState && /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: {
            sectionname: 'groups'
        }
    }, /*#__PURE__*/ React.createElement(FoldableGroupList, {
        sectionType: "watched_groups",
        streamId: watchedGroupsStreamId,
        tabIndex: tabIndex,
        selectedItem: selectedItem,
        isCollapsed: isCollapsed,
        selectedItemClassName: s['selected-item'],
        icon: AddToGroupFill,
        title: t('networkBar.subheader.groups'),
        toggleAriaLabel: t('networkBar.subheader.groups'),
        className: s['group']
    })), showRecommendedGroups && /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: {
            sectionname: 'recommended groups'
        }
    }, /*#__PURE__*/ React.createElement(FoldableGroupList, {
        sectionType: "recommended_groups",
        streamId: recommendedGroupsStreamId,
        tabIndex: tabIndex,
        selectedItem: selectedItem,
        isCollapsed: isCollapsed,
        selectedItemClassName: s['selected-item'],
        className: s['group'],
        icon: RecommendedIcon,
        title: t('networkBar.subheader.recommendedGroups'),
        toggleAriaLabel: t('networkBar.subheader.recommendedGroups')
    })), isLoggedIn && /*#__PURE__*/ React.createElement("div", {
        className: s['group']
    }, /*#__PURE__*/ React.createElement(NetworkBarLink, {
        label: "feedback",
        url: NETWORKBAR_FEEDBACK_URL,
        icon: GiveFeedbackIcon,
        tabIndex: tabIndex,
        isCollapsed: !isMobile && isCollapsed
    })))));
};
NetworkBar.displayName = 'NetworkBar';
export default NetworkBar;
